import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { EmailBrandingCard } from '@/components/email-identity/email-branding-card'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { useViewOnly } from '@/components/templates/app-shell'
import type {
  EmailIdentitySettingsResponse,
  SendingDomainResponse,
} from '@/pages/EmailIdentitySettingsPage.types'
import { SendingDomainDetails } from '@/pages/SendingDomainDetails'

export function EmailIdentitySettingsPage() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [senderName, setSenderName] = React.useState('')
  const [replyToAddress, setReplyToAddress] = React.useState('')
  const [logoUrl, setLogoUrl] = React.useState('')
  const [accentColor, setAccentColor] = React.useState('')
  const [postalAddress, setPostalAddress] = React.useState('')
  const [saved, setSaved] = React.useState(false)
  const [domain, setDomain] = React.useState('')
  const settingsQuery = useQuery({
    queryKey: ['email-identity-settings'],
    queryFn: () => getJSON<EmailIdentitySettingsResponse>('/api/v1/email_identity_settings'),
    retry: false,
  })
  const updateSettings = useMutation({
    mutationFn: () =>
      patchJSON<EmailIdentitySettingsResponse>('/api/v1/email_identity_settings', {
        email_identity_settings: {
          sender_name: senderName,
          reply_to_address: replyToAddress,
          logo_url: logoUrl,
          accent_color: accentColor,
          postal_address: postalAddress,
        },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['email-identity-settings'], data)
      setSenderName(data.email_identity_settings.sender_name)
      setReplyToAddress(data.email_identity_settings.reply_to_address ?? '')
      setLogoUrl(data.email_identity_settings.logo_url ?? '')
      setAccentColor(data.email_identity_settings.accent_color ?? '')
      setPostalAddress(data.email_identity_settings.postal_address ?? '')
      setSaved(true)
    },
  })
  const domainQuery = useQuery({
    queryKey: ['sending-domain'],
    queryFn: () => getJSON<SendingDomainResponse>('/api/v1/sending_domain'),
    retry: false,
  })
  const registerDomain = useMutation({
    mutationFn: () =>
      postJSON<SendingDomainResponse>('/api/v1/sending_domain', {
        sending_domain: { domain },
      }),
    onSuccess: (data) => queryClient.setQueryData(['sending-domain'], data),
  })
  const verifyDomain = useMutation({
    mutationFn: () => postJSON<SendingDomainResponse>('/api/v1/sending_domain/verify', {}),
    onSuccess: (data) => queryClient.setQueryData(['sending-domain'], data),
  })

  React.useEffect(() => {
    const settings = settingsQuery.data?.email_identity_settings
    if (!settings) return

    setSenderName(settings.sender_name)
    setReplyToAddress(settings.reply_to_address ?? '')
    setLogoUrl(settings.logo_url ?? '')
    setAccentColor(settings.accent_color ?? '')
    setPostalAddress(settings.postal_address ?? '')
  }, [settingsQuery.data])

  const changeField = (change: () => void) => {
    change()
    setSaved(false)
    updateSettings.reset()
  }

  return (
    <EmailIdentitySettingsContent
      {...{
        settingsQuery,
        viewOnly,
        senderName,
        replyToAddress,
        logoUrl,
        accentColor,
        postalAddress,
        saved,
        setSaved,
        updateSettings,
        domain,
        domainQuery,
        registerDomain,
        verifyDomain,
        changeField,
        setSenderName,
        setReplyToAddress,
        setLogoUrl,
        setAccentColor,
        setPostalAddress,
        setDomain,
      }}
    />
  )
}

type EmailIdentitySettingsContentProps = {
  settingsQuery: { isLoading: boolean; isError: boolean; data?: EmailIdentitySettingsResponse }
  viewOnly: boolean
  senderName: string
  replyToAddress: string
  logoUrl: string
  accentColor: string
  postalAddress: string
  saved: boolean
  setSaved: (value: boolean) => void
  updateSettings: { isPending: boolean; error: Error | null; mutate: () => void }
  domain: string
  domainQuery: { isLoading: boolean; isError: boolean; data?: SendingDomainResponse }
  registerDomain: { error: Error | null; isPending: boolean; mutate: () => void }
  verifyDomain: { isError: boolean; isPending: boolean; mutate: () => void }
  changeField: (change: () => void) => void
  setSenderName: (value: string) => void
  setReplyToAddress: (value: string) => void
  setLogoUrl: (value: string) => void
  setAccentColor: (value: string) => void
  setPostalAddress: (value: string) => void
  setDomain: (value: string) => void
}

function EmailIdentitySettingsContent({
  settingsQuery,
  viewOnly,
  senderName,
  replyToAddress,
  logoUrl,
  accentColor,
  postalAddress,
  saved,
  setSaved,
  updateSettings,
  domain,
  domainQuery,
  registerDomain,
  verifyDomain,
  changeField,
  setSenderName,
  setReplyToAddress,
  setLogoUrl,
  setAccentColor,
  setPostalAddress,
  setDomain,
}: EmailIdentitySettingsContentProps) {
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <h1 className="text-heading-sm font-semibold text-foreground">Email identity</h1>
      </header>
      {settingsQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading email identity...
        </p>
      ) : settingsQuery.isError ? (
        <Alert>Email identity unavailable</Alert>
      ) : (
        <Card className="max-w-2xl">
          <CardContent className="divide-y divide-border p-0" data-testid="email-identity-panel">
            <EmailBrandingCard
              senderName={senderName}
              replyToAddress={replyToAddress}
              logoUrl={logoUrl}
              accentColor={accentColor}
              postalAddress={postalAddress}
              setupTask={settingsQuery.data?.email_identity_settings.setup_task ?? null}
              saved={saved}
              pending={updateSettings.isPending}
              error={updateSettings.error}
              onSenderNameChange={(value: string) =>
                !viewOnly && changeField(() => setSenderName(value))
              }
              onReplyToChange={(value: string) =>
                !viewOnly && changeField(() => setReplyToAddress(value))
              }
              onLogoUrlChange={(value: string) => !viewOnly && changeField(() => setLogoUrl(value))}
              onAccentColorChange={(value: string) =>
                !viewOnly && changeField(() => setAccentColor(value))
              }
              onPostalAddressChange={(value: string) =>
                !viewOnly && changeField(() => setPostalAddress(value))
              }
              onSubmit={() => {
                if (!viewOnly) {
                  setSaved(false)
                  updateSettings.mutate()
                }
              }}
              readOnly={viewOnly}
            />
            <SendingDomainSection
              domain={domain}
              sendingDomain={domainQuery.data?.sending_domain}
              loading={domainQuery.isLoading}
              loadError={domainQuery.isError}
              registrationError={registerDomain.error}
              registrationPending={registerDomain.isPending}
              verificationError={verifyDomain.isError}
              verificationPending={verifyDomain.isPending}
              onDomainChange={setDomain}
              onRegister={() => !viewOnly && registerDomain.mutate()}
              onVerify={() => !viewOnly && verifyDomain.mutate()}
              viewOnly={viewOnly}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function SendingDomainSection({
  viewOnly,
  domain,
  sendingDomain,
  loading,
  loadError,
  registrationError,
  registrationPending,
  verificationError,
  verificationPending,
  onDomainChange,
  onRegister,
  onVerify,
}: {
  viewOnly: boolean
  domain: string
  sendingDomain: SendingDomainResponse['sending_domain'] | undefined
  loading: boolean
  loadError: boolean
  registrationError: Error | null
  registrationPending: boolean
  verificationError: boolean
  verificationPending: boolean
  onDomainChange: (value: string) => void
  onRegister: () => void
  onVerify: () => void
}) {
  return (
    <section className="p-4">
      <h2 className="pb-3 text-body font-semibold">Sending Domain</h2>
      <SendingDomainCardContent
        domain={domain}
        sendingDomain={sendingDomain}
        loading={loading}
        loadError={loadError}
        registrationError={registrationError}
        registrationPending={registrationPending}
        verificationError={verificationError}
        verificationPending={verificationPending}
        onDomainChange={onDomainChange}
        onRegister={onRegister}
        onVerify={onVerify}
        viewOnly={viewOnly}
      />
    </section>
  )
}

function SendingDomainCardContent({
  domain,
  sendingDomain,
  loading,
  loadError,
  registrationError,
  registrationPending,
  verificationError,
  verificationPending,
  onDomainChange,
  onRegister,
  onVerify,
  viewOnly,
}: Parameters<typeof SendingDomainSection>[0]) {
  if (loading) {
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading Sending Domain...
      </p>
    )
  }
  if (loadError) return <Alert>Sending Domain unavailable</Alert>
  if (sendingDomain) {
    return (
      <>
        <SendingDomainDetails
          sendingDomain={sendingDomain}
          pending={verificationPending}
          onVerify={onVerify}
          viewOnly={viewOnly}
        />
        {verificationError && <Alert>DNS verification unavailable</Alert>}
      </>
    )
  }

  const fieldError =
    apiFieldMessages(registrationError, 'domain')[0] ??
    apiFieldMessages(registrationError, 'association_id')[0]
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        onRegister()
      }}
    >
      <FormField
        label="Sending Domain"
        htmlFor="sending-domain"
        hint="Subdomain · mail.example.org"
        error={fieldError}
      >
        <Input
          disabled={viewOnly}
          className="h-9"
          id="sending-domain"
          value={domain}
          onChange={(event) => onDomainChange(event.target.value)}
        />
      </FormField>
      {!viewOnly && (
        <Button type="submit" variant="outline" size="sm" disabled={registrationPending}>
          {registrationPending ? 'Registering...' : 'Register Sending Domain'}
        </Button>
      )}
      {registrationError && !fieldError && <Alert>Sending Domain registration unavailable</Alert>}
    </form>
  )
}
